// IntInput — text input that stores integers as numbers.
//
// Mirrors the MoneyInput pattern. The old `<input type="number" value={n}
// onChange={... Number(e.target.value) || fallback}>` form re-formatted the
// displayed value on every keystroke: starting at "12" and trying to type
// "36" required select-all because clearing a digit would immediately reset
// to the fallback. The spinner arrows aren't useful for typed integers.
//
// This component keeps a LOCAL raw-string state and only normalizes on blur.
// onChange still fires on every keystroke so cascading recomputes (e.g.
// leaseEndDate from leaseTermMonths) see a fresh value, but for intermediate
// non-parseable input we pass the floor (min ?? 0) so callers never see NaN.
// The visible text is never reformatted while the user is typing.

import { useEffect, useRef, useState } from 'react';

interface IntInputProps {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel?: string;
  /** Optional id forwarded to the underlying input — required for label htmlFor. */
  id?: string;
}

function intToDisplayString(n: number): string {
  if (!Number.isFinite(n)) return '';
  return String(Math.trunc(n));
}

function parseIntLoose(s: string): number | null {
  const trimmed = s.trim();
  if (trimmed === '' || trimmed === '-') return null;
  const n = parseInt(trimmed, 10);
  if (!Number.isFinite(n)) return null;
  return n;
}

function clamp(n: number, min: number | undefined, max: number | undefined): number {
  let out = n;
  if (typeof min === 'number' && out < min) out = min;
  if (typeof max === 'number' && out > max) out = max;
  return out;
}

export function IntInput({
  value,
  onChange,
  min,
  max,
  className = 'input',
  placeholder,
  disabled,
  readOnly,
  ariaLabel,
  id,
}: IntInputProps) {
  const [raw, setRaw] = useState<string>(() => intToDisplayString(value));
  // Track which numeric value our raw represents. When the external `value`
  // changes from a non-user source (draft load, cascading recompute from
  // another field), re-sync the display. When it changes because of OUR
  // onChange, raw is already current; don't clobber mid-typing.
  const lastSyncedValue = useRef<number>(value);

  useEffect(() => {
    if (value !== lastSyncedValue.current) {
      const parsedFromRaw = parseIntLoose(raw);
      if (parsedFromRaw !== value) {
        setRaw(intToDisplayString(value));
      }
      lastSyncedValue.current = value;
    }
  }, [value, raw]);

  return (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      aria-label={ariaLabel}
      value={raw}
      onChange={(e) => {
        const newRaw = e.target.value;
        setRaw(newRaw);
        const parsed = parseIntLoose(newRaw);
        // For empty / partial input we surface the floor (min ?? 0) so
        // cascading callers (e.g. computeEndDateFromTerm) never receive NaN.
        // We deliberately do NOT clamp on every keystroke — that would make
        // "0" → "10" impossible when min=1.
        const next = parsed === null ? (min ?? 0) : parsed;
        lastSyncedValue.current = next;
        onChange(next);
      }}
      onBlur={() => {
        const parsed = parseIntLoose(raw);
        const clamped = clamp(parsed === null ? (min ?? 0) : parsed, min, max);
        const normalized = intToDisplayString(clamped);
        if (normalized !== raw) setRaw(normalized);
        if (clamped !== lastSyncedValue.current) {
          lastSyncedValue.current = clamped;
          onChange(clamped);
        }
      }}
    />
  );
}
